import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ProductCard from '@/components/feature/ProductCard';
import { categories, products } from '@/mocks/products';

export default function Category() {
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const navigate = useNavigate();

  const filteredProducts = useMemo(() => {
    if (!activeSlug) return products;
    return products.filter((p) => p.categorySlug === activeSlug);
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const handleCategoryClick = (slug: string) => {
    setActiveSlug(slug);
    navigate(`/category/${slug}`);
  };

  return (
    <>
      <PageMeta
        title="全部分类 - 爱威奶在线 | 爱威奶产品系列"
        description="浏览爱威奶全系列产品分类：纯牛奶系列、酸奶系列、儿童牛奶系列、高钙奶系列、低脂牛奶系列，找到适合您的优质牛奶产品。"
        keywords="爱威奶产品系列,爱威奶分类,纯牛奶,酸奶,儿童牛奶,高钙奶,低脂牛奶"
        canonicalUrl="https://www.aiweinai.com/category"
      />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={[
            { label: '首页', to: '/' },
            { label: activeCategory ? activeCategory.name : '全部分类' },
          ]} />

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
              {activeCategory ? activeCategory.name : '全部分类'}
            </h1>
            <p className="text-sm text-foreground-500">
              {activeCategory ? activeCategory.description : '浏览爱威奶全系列产品，找到最适合您的那一款'}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mb-10">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => { setActiveSlug(null); navigate('/category'); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  !activeSlug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} {...product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无产品</p>
            </div>
          )}

          {/* Category Descriptions */}
          {activeCategory && (
            <div className="mt-16 bg-background-100 rounded-lg p-6 md:p-8">
              <h2 className="text-lg font-semibold text-foreground-900 mb-3 font-heading">
                关于{activeCategory.name}
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {activeCategory.description}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "全部分类 - 爱威奶在线",
          "description": "浏览爱威奶全系列产品分类，找到适合您的优质牛奶产品。",
          "url": "https://www.aiweinai.com/category",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.aiweinai.com" },
              { "@type": "ListItem", "position": 2, "name": activeCategory ? activeCategory.name : "全部分类", "item": "https://www.aiweinai.com/category" },
            ],
          },
        }),
      }} />
    </>
  );
}